import { useEffect } from 'react'
import { ApiCustomer } from '@vend/types'
import { PaymentRequestQueryResponse } from '../../../../../functions/invoicing/fetch-invoicing-payment-requests'
import { bulkCancelPaymentRequests } from '../../../../../functions/invoicing/bulk-cancel-payment-requests'
import { PaymentRequestStatus } from '../../../../../functions/invoicing/payment-request-types'
import { FEATURE_ENABLE_INVOICING_PAYMENT_METHOD_SETTINGS } from '../../../../../constants'
import { getFeatureFromCache } from '../../../../../util/feature-cache'
import { useAppDispatch } from '../../../../../state/hooks'
import { setPaymentRequestsAutoCancelled } from '../../../../../state/slices/currentSale/currentSaleSlice'

interface UseAutoCancelPaymentRequestsParams {
  paymentRequests: PaymentRequestQueryResponse[]
  outstandingBalance: number
  saleLocalId: string | null
  customer: ApiCustomer | null
  refetchPaymentRequests: () => Promise<void>
}

/**
 * Auto-cancels cancellable pending payment requests when their total exceeds the
 * amount that can still be collected, to avoid overpayment scenarios.
 *
 * In-transit requests (an ACH direct debit payment already in flight, i.e.
 * `processingBalance > 0`) cannot be cancelled. They are excluded from the
 * cancellable set and their committed funds are subtracted from the outstanding
 * balance before the comparison is made.
 *
 * @param paymentRequests - Array of payment requests for the sale
 * @param outstandingBalance - Current outstanding balance of the sale
 * @param saleLocalId - The local ID of the sale
 * @param customer - The customer associated with the sale
 * @param refetchPaymentRequests - Function to refetch payment requests after cancellation
 */
export const useAutoCancelPaymentRequests = ({
  paymentRequests,
  outstandingBalance,
  saleLocalId,
  customer,
  refetchPaymentRequests,
}: UseAutoCancelPaymentRequestsParams) => {
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (paymentRequests.length === 0 || !saleLocalId || !customer?.id) {
      return
    }

    const inTransitHandlingEnabled = getFeatureFromCache(
      FEATURE_ENABLE_INVOICING_PAYMENT_METHOD_SETTINGS
    )

    const pendingRequests = paymentRequests.filter(
      request =>
        request.status === PaymentRequestStatus.SENT &&
        (!inTransitHandlingEnabled || (request.processingBalance ?? 0) === 0)
    )

    if (pendingRequests.length === 0) {
      return
    }

    const totalInTransitAmount = (inTransitHandlingEnabled
      ? paymentRequests
      : []
    ).reduce((sum, request) => sum + (request.processingBalance ?? 0) / 100, 0)

    const totalPendingAmount = pendingRequests.reduce(
      (sum, request) => sum + request.requestedAmount / 100,
      0
    )

    if (totalPendingAmount > outstandingBalance - totalInTransitAmount) {
      dispatch(setPaymentRequestsAutoCancelled(true))

      bulkCancelPaymentRequests(saleLocalId, customer.id).then(result => {
        if (result.success) {
          refetchPaymentRequests()
        }
      })
    }
  }, [
    outstandingBalance,
    paymentRequests,
    saleLocalId,
    customer,
    refetchPaymentRequests,
    dispatch,
  ])
}
